import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import api from '../services/api';
import { formatParsedResult, parsedForSample } from '../utils/formatParsed';
import { verdictColors, verdictScoreText } from '../utils/verdictPresentation';

/* ─────────────────────────────────────────────────────────────── */
/*  Per-sample row (validation preview)                            */
/* ─────────────────────────────────────────────────────────────── */

const STATUS_ICON = {
  fixed: { Icon: CheckCircleIcon, color: '#10b981', label: 'Fixed' },
  unfixed: { Icon: ErrorIcon, color: '#ef4444', label: 'Unfixed' },
  passing: { Icon: CheckCircleIcon, color: '#64748b', label: 'Passing' },
  regression: { Icon: ReportProblemIcon, color: '#f97316', label: 'Regression' },
};

function formatSampleValue(v) {
  if (v == null) return 'null';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}

const longTextSx = {
  wordBreak: 'break-all',
  overflowWrap: 'anywhere',
};

function parsedChipSx(validation) {
  if (!validation?.supported) {
    return {
      color: '#f59e0b',
      background: 'rgba(245,158,11,0.08)',
      border: '1px solid rgba(245,158,11,0.2)',
    };
  }
  if ((validation.checked || 0) > 0 && (validation.matched || 0) < validation.checked) {
    return {
      color: '#ef4444',
      background: 'rgba(239,68,68,0.08)',
      border: '1px solid rgba(239,68,68,0.2)',
    };
  }
  return {
    color: '#10b981',
    background: 'rgba(16,185,129,0.08)',
    border: '1px solid rgba(16,185,129,0.2)',
  };
}

function SampleRow({ result, parsed }) {
  const cfg = STATUS_ICON[result.status] || STATUS_ICON.unfixed;
  const { Icon, color, label } = cfg;
  const parsedLine = formatParsedResult(parsed);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      py: 0.75, px: 1.5, mb: 0.5, borderRadius: 1,
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.05)',
      overflow: 'hidden',
    }}>
      <Icon sx={{ color, fontSize: 14, mt: 0.3, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={label} size="small" sx={{
            height: 18, fontSize: '0.68rem', fontWeight: 600, color, px: 0,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
          }} />
          {result.is_passing_sample && (
            <Chip label="regression test" size="small" sx={{
              height: 18, fontSize: '0.65rem', color: '#64748b',
              background: 'transparent', border: '1px solid rgba(255,255,255,0.06)',
            }} />
          )}
        </Box>
        <Typography sx={{
          color: '#60a5fa', fontSize: '0.72rem', mt: 0.3,
          ...longTextSx,
        }}>
          {result.html_url}
        </Typography>
        {(result.expected_value != null || result.extracted_value != null || parsedLine) && (
          <Box sx={{ mt: 0.25, display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0 }}>
            {result.expected_value != null && (
              <Typography sx={{ color: '#475569', fontSize: '0.7rem', lineHeight: 1.45, ...longTextSx }}>
                expected: <span style={{ color: '#94a3b8' }}>{formatSampleValue(result.expected_value)}</span>
              </Typography>
            )}
            {(result.expected_value != null || result.extracted_value != null) && (
              <Typography sx={{ color: '#475569', fontSize: '0.7rem', lineHeight: 1.45, ...longTextSx }}>
                extracted:{' '}
                <span style={{ color: result.extracted_value != null ? '#94a3b8' : '#ef4444' }}>
                  {formatSampleValue(result.extracted_value)}
                </span>
              </Typography>
            )}
            {parsedLine && (
              <Typography sx={{ color: '#475569', fontSize: '0.7rem', lineHeight: 1.45, ...longTextSx }}>
                parsed:{' '}
                <span style={{ color: parsedLine.ok ? '#94a3b8' : '#ef4444' }}>
                  {parsedLine.text}
                </span>
              </Typography>
            )}
          </Box>
        )}
        {result.reason && (
          <Typography sx={{ color: '#ef4444', fontSize: '0.68rem', mt: 0.2, ...longTextSx }}>
            {result.reason}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Coverage (Tier B) / Regression (Tier A) sample rows — visually  */
/*  differentiated from Target Samples via a colored accent so     */
/*  reviewers can tell at a glance which check each row belongs to */
/* ─────────────────────────────────────────────────────────────── */

const SECTION_COLORS = {
  target: '#60a5fa',
  coverage: '#a78bfa',
  regression: '#2dd4bf',
};

function SectionHeader({ label, accent, count }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }} />
      <Typography sx={{ color: accent, fontSize: '0.76rem', fontWeight: 700 }}>
        {label}
      </Typography>
      {count != null && (
        <Typography sx={{ color: '#475569', fontSize: '0.7rem' }}>{count}</Typography>
      )}
    </Box>
  );
}

function CoverageSampleRow({ result }) {
  const color = result.passing ? '#10b981' : '#ef4444';
  const Icon = result.passing ? CheckCircleIcon : ErrorIcon;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      py: 0.75, px: 1.5, mb: 0.5, borderRadius: 1,
      background: 'rgba(167,139,250,0.04)',
      border: '1px solid rgba(167,139,250,0.12)',
      borderLeftWidth: 2, borderLeftColor: SECTION_COLORS.coverage,
      overflow: 'hidden',
    }}>
      <Icon sx={{ color, fontSize: 14, mt: 0.3, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Typography sx={{
          color: '#60a5fa', fontSize: '0.72rem',
          ...longTextSx,
        }}>
          {result.html_url}
        </Typography>
        <Typography sx={{ color: '#475569', fontSize: '0.7rem', mt: 0.2, lineHeight: 1.45, ...longTextSx }}>
          {result.confirmed_absent ? (
            <span style={{ color: '#94a3b8' }}>confirmed absent from page — null is correct</span>
          ) : (
            <>extracted: <span style={{ color: result.passing ? '#94a3b8' : '#ef4444' }}>{formatSampleValue(result.extracted_value)}</span></>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

function RegressionSampleRow({ result }) {
  const color = result.matched ? '#10b981' : '#ef4444';
  const Icon = result.matched ? CheckCircleIcon : ErrorIcon;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      py: 0.75, px: 1.5, mb: 0.5, borderRadius: 1,
      background: 'rgba(45,212,191,0.04)',
      border: '1px solid rgba(45,212,191,0.12)',
      borderLeftWidth: 2, borderLeftColor: SECTION_COLORS.regression,
      overflow: 'hidden',
    }}>
      <Icon sx={{ color, fontSize: 14, mt: 0.3, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Typography sx={{
          color: '#60a5fa', fontSize: '0.72rem',
          ...longTextSx,
        }}>
          {result.html_url}
        </Typography>
        <Box sx={{ mt: 0.2, display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0 }}>
          <Typography sx={{ color: '#475569', fontSize: '0.7rem', lineHeight: 1.45, ...longTextSx }}>
            db value: <span style={{ color: '#94a3b8' }}>{formatSampleValue(result.expected_value)}</span>
          </Typography>
          <Typography sx={{ color: '#475569', fontSize: '0.7rem', lineHeight: 1.45, ...longTextSx }}>
            extracted:{' '}
            <span style={{ color: result.matched ? '#94a3b8' : '#ef4444' }}>
              {formatSampleValue(result.extracted_value)}
            </span>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  EditConfigModal                                                */
/* ─────────────────────────────────────────────────────────────── */

export default function EditConfigModal({
  open,
  onClose,
  jobId,
  job,
  initialConfig,
  initialParsedConfig,
  parsedConfigPath,
  showParsedConfig = false,
  onApproveWithEdits,
  approving = false,
  approveBlockReason = '',
}) {
  const [configText, setConfigText] = useState('');
  const [parsedConfigText, setParsedConfigText] = useState('');
  const [configParseError, setConfigParseError] = useState(null);
  const [parsedConfigParseError, setParsedConfigParseError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Reset state whenever modal opens with new config
  useEffect(() => {
    if (open) {
      setConfigText(JSON.stringify(initialConfig || [], null, 2));
      setParsedConfigText(JSON.stringify(initialParsedConfig || {}, null, 2));
      setConfigParseError(null);
      setParsedConfigParseError(null);
      setValidationResult(null);
      setValidationError(null);
    }
  }, [open, initialConfig, initialParsedConfig]);

  function handleTextChange(e) {
    setConfigText(e.target.value);
    setConfigParseError(null);
    setValidationResult(null);
    setValidationError(null);
  }

  function handleParsedConfigTextChange(e) {
    setParsedConfigText(e.target.value);
    setParsedConfigParseError(null);
    setValidationResult(null);
    setValidationError(null);
  }

  function parseConfig() {
    try {
      const parsed = JSON.parse(configText);
      if (!Array.isArray(parsed)) {
        setConfigParseError('Extraction config must be a JSON array.');
        return null;
      }
      setConfigParseError(null);
      return parsed;
    } catch (err) {
      setConfigParseError(`Invalid JSON: ${err.message}`);
      return null;
    }
  }

  function parseParsedConfig() {
    if (!showParsedConfig) return null;
    try {
      const parsed = JSON.parse(parsedConfigText);
      if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') {
        setParsedConfigParseError('Parsed config must be a JSON object.');
        return undefined;
      }
      setParsedConfigParseError(null);
      return parsed;
    } catch (err) {
      setParsedConfigParseError(`Invalid JSON: ${err.message}`);
      return undefined;
    }
  }

  function parseEdits() {
    const config = parseConfig();
    const parsedConfig = parseParsedConfig();
    if (!config || parsedConfig === undefined) return null;
    return { config, parsedConfig };
  }

  async function handleValidate() {
    const edits = parseEdits();
    if (!edits) return;
    setValidating(true);
    setValidationResult(null);
    setValidationError(null);
    try {
      const result = await api.validateHealJobConfig(
        jobId,
        edits.config,
        edits.parsedConfig,
      );
      setValidationResult(result);
    } catch (err) {
      setValidationError(err.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  }

  function handleApprove() {
    const edits = parseEdits();
    if (!edits) return;
    onApproveWithEdits(edits.config, edits.parsedConfig);
  }

  const tierA = validationResult?.tier_a_summary;
  const allFixed = tierA && tierA.unfixed === 0 && tierA.regressions === 0;
  const coverageOk =
    validationResult?.coverage_verdict == null ||
    validationResult.coverage_verdict === 'PASS';
  const regressionOk =
    validationResult?.regression_verdict == null ||
    validationResult.regression_verdict === 'PASS';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          background: '#0f172a',
          color: '#e2e8f0',
          border: '1px solid rgba(148,163,184,0.12)',
          borderRadius: 2,
          width: '90vw',
          maxWidth: '90vw',
          height: '88vh',
          maxHeight: '88vh',
          m: 2,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 3, py: 1.5, borderBottom: '1px solid rgba(148,163,184,0.08)', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#e2e8f0' }}>
            Edit Config
          </Typography>
          {job && (
            <Typography sx={{ color: '#475569', fontSize: '0.8rem' }}>
              {job.retailer} / {job.attribute}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#64748b', '&:hover': { color: '#94a3b8' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', gap: 0, p: 0 }}>

        {/* Left: JSON editor */}
        <Box sx={{
          width: validationResult ? '50%' : '100%',
          display: 'flex', flexDirection: 'column',
          borderRight: validationResult ? '1px solid rgba(255,255,255,0.06)' : 'none',
          transition: 'width 0.2s',
        }}>
          <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500, mb: 0.5 }}>
              Extraction config (JSON array)
            </Typography>
            {configParseError && (
              <Typography sx={{ color: '#ef4444', fontSize: '0.72rem', mb: 0.5 }}>
                {configParseError}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, px: 3, pb: 2, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <textarea
              value={configText}
              onChange={handleTextChange}
              spellCheck={false}
              style={{
                width: '100%',
                minHeight: showParsedConfig ? 240 : 300,
                flex: showParsedConfig ? '1 1 50%' : '1 1 auto',
                background: 'rgba(255,255,255,0.03)',
                border: configParseError
                  ? '1px solid rgba(239,68,68,0.4)'
                  : '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6,
                color: '#e2e8f0',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                lineHeight: 1.6,
                padding: '10px 12px',
                resize: 'none',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {showParsedConfig && (
              <Box sx={{ flex: '1 1 50%', minHeight: 240, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500, mb: 0.5 }}>
                  Parsed config (JSON object)
                </Typography>
                {parsedConfigPath && (
                  <Typography sx={{
                    color: '#64748b', fontSize: '0.68rem', fontFamily: 'monospace',
                    mb: 0.5, wordBreak: 'break-all',
                  }}>
                    {parsedConfigPath}
                  </Typography>
                )}
                {parsedConfigParseError && (
                  <Typography sx={{ color: '#ef4444', fontSize: '0.72rem', mb: 0.5 }}>
                    {parsedConfigParseError}
                  </Typography>
                )}
                <textarea
                  value={parsedConfigText}
                  onChange={handleParsedConfigTextChange}
                  spellCheck={false}
                  style={{
                    width: '100%',
                    minHeight: 180,
                    flex: 1,
                    background: 'rgba(255,255,255,0.03)',
                    border: parsedConfigParseError
                      ? '1px solid rgba(239,68,68,0.4)'
                      : '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 6,
                    color: '#e2e8f0',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    lineHeight: 1.6,
                    padding: '10px 12px',
                    resize: 'none',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Right: validation results */}
        {validationResult && (
          <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>
                  Validation Results
                </Typography>
                {tierA && (
                  <>
                    <Chip
                      label={`${tierA.fixed}/${tierA.total_failing} fixed`}
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.68rem', fontWeight: 600,
                        color: tierA.fixed === tierA.total_failing ? '#10b981' : '#f59e0b',
                        background: tierA.fixed === tierA.total_failing
                          ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)',
                        border: `1px solid ${tierA.fixed === tierA.total_failing
                          ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)'}`,
                      }}
                    />
                    {tierA.regressions > 0 && (
                      <Chip
                        label={`${tierA.regressions} regression${tierA.regressions !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.68rem', fontWeight: 600,
                          color: '#f97316', background: 'rgba(249,115,22,0.08)',
                          border: '1px solid rgba(249,115,22,0.2)',
                        }}
                      />
                    )}
                  </>
                )}
                {validationResult.coverage_verdict && (
                  <Chip
                    label={`Coverage: ${verdictScoreText(validationResult.coverage_verdict, validationResult.coverage_score)}`}
                    size="small"
                    sx={{
                      height: 20, fontSize: '0.68rem', fontWeight: 600,
                      color: verdictColors(validationResult.coverage_verdict, 0.2).color,
                      background: verdictColors(validationResult.coverage_verdict, 0.2).bg,
                      border: `1px solid ${verdictColors(validationResult.coverage_verdict, 0.2).border}`,
                    }}
                  />
                )}
                {validationResult.regression_verdict && (
                  <Chip
                    label={`Regression: ${verdictScoreText(validationResult.regression_verdict, validationResult.regression_score)}`}
                    size="small"
                    sx={{
                      height: 20, fontSize: '0.68rem', fontWeight: 600,
                      color: verdictColors(validationResult.regression_verdict, 0.2).color,
                      background: verdictColors(validationResult.regression_verdict, 0.2).bg,
                      border: `1px solid ${verdictColors(validationResult.regression_verdict, 0.2).border}`,
                    }}
                  />
                )}
                {validationResult.parsed_config_validation && (
                  <Chip
                    label={validationResult.parsed_config_validation.supported
                      ? `Parsed: ${validationResult.parsed_config_validation.matched}/${validationResult.parsed_config_validation.checked} matched`
                      : 'Parsed: human review required'}
                    size="small"
                    sx={{
                      height: 20, fontSize: '0.68rem', fontWeight: 600,
                      ...parsedChipSx(validationResult.parsed_config_validation),
                    }}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 2 }}>
              {validationResult.parsed_config_validation?.reason && (
                <Typography sx={{
                  color: '#f59e0b', fontSize: '0.72rem', lineHeight: 1.5,
                  mb: 1.5, p: 1.25, borderRadius: 1,
                  background: 'rgba(245,158,11,0.06)',
                  border: '1px solid rgba(245,158,11,0.15)',
                }}>
                  {validationResult.parsed_config_validation.reason}
                </Typography>
              )}
              {validationResult.per_sample_results.length > 0 && (
                <>
                  {(validationResult.coverage_samples?.length > 0 || validationResult.regression_samples?.length > 0) && (
                    <SectionHeader label="Target Samples" accent={SECTION_COLORS.target} />
                  )}
                  {validationResult.per_sample_results.map((r, i) => (
                    <SampleRow
                      key={i}
                      result={r}
                      parsed={parsedForSample(validationResult.parsed_config_validation, r)}
                    />
                  ))}
                </>
              )}

              {validationResult.coverage_samples?.length > 0 && (
                <>
                  <SectionHeader
                    label="Coverage Samples (Tier B)"
                    accent={SECTION_COLORS.coverage}
                    count={`${validationResult.coverage_samples.length} unlabeled`}
                  />
                  {validationResult.coverage_samples.map((r, i) => (
                    <CoverageSampleRow key={i} result={r} />
                  ))}
                </>
              )}

              {validationResult.regression_samples?.length > 0 && (
                <>
                  <SectionHeader
                    label="Regression Samples (Tier A)"
                    accent={SECTION_COLORS.regression}
                    count={`${validationResult.regression_samples.length} labeled`}
                  />
                  {validationResult.regression_samples.map((r, i) => (
                    <RegressionSampleRow key={i} result={r} />
                  ))}
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 3, py: 1.5, borderTop: '1px solid rgba(148,163,184,0.08)', flexShrink: 0, gap: 1.5,
      }}>
        {validationError && (
          <Typography sx={{ color: '#ef4444', fontSize: '0.75rem', flex: 1 }}>
            {validationError}
          </Typography>
        )}
        {!validationError && validationResult && (
          <Typography sx={{ color: allFixed && coverageOk && regressionOk ? '#10b981' : '#f59e0b', fontSize: '0.75rem', flex: 1 }}>
            {allFixed && coverageOk && regressionOk
              ? 'All samples pass with this config.'
              : 'Some samples still failing — you can still approve, or keep editing.'}
          </Typography>
        )}
        {!validationError && !validationResult && (
          <Typography sx={{ color: '#475569', fontSize: '0.75rem', flex: 1 }}>
            Edit the config then validate before approving.
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
          <Button
            size="small" onClick={onClose}
            sx={{
              color: '#94a3b8', textTransform: 'none', fontWeight: 500, fontSize: '0.82rem',
              px: 2, borderRadius: 1, border: '1px solid rgba(255,255,255,0.08)',
              '&:hover': { background: 'rgba(255,255,255,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            size="small" onClick={handleValidate}
            disabled={validating || !!configParseError || !!parsedConfigParseError}
            sx={{
              color: '#60a5fa', textTransform: 'none', fontWeight: 500, fontSize: '0.82rem',
              px: 2, borderRadius: 1, border: '1px solid rgba(59,130,246,0.3)',
              '&:hover': { background: 'rgba(59,130,246,0.06)' },
              '&.Mui-disabled': { color: 'rgba(96,165,250,0.4)' },
            }}
          >
            {validating ? <CircularProgress size={14} sx={{ color: '#60a5fa' }} /> : 'Validate'}
          </Button>
          <Tooltip title={approveBlockReason} arrow>
            <span>
              <Button
                variant="contained" size="small"
                onClick={handleApprove}
                disabled={approving || Boolean(approveBlockReason) || !validationResult || !!configParseError || !!parsedConfigParseError}
                sx={{
                  background: '#10b981', fontWeight: 500, textTransform: 'none', px: 2.5,
                  fontSize: '0.82rem', boxShadow: 'none',
                  '&:hover': { background: '#059669', boxShadow: 'none' },
                  '&.Mui-disabled': { background: 'rgba(16,185,129,0.3)', color: 'rgba(255,255,255,0.4)' },
                }}
              >
                {approving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Approve with Edits'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Dialog>
  );
}
